// react
import { createContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// utils
import PropTypes from 'prop-types';
import axios from '../utils/axios';
import { isValidToken, setSession } from '../utils/jwt';
// actions
import * as actionsAuth from '../redux/auth/actions';
import * as alertsActions from '../redux/slices/mycore/alertsActions';
// analytics
import { identifyUser, resetAnalytics, track } from '../utils/analytics';

// ----------------------------------------------------------------------

const AuthContext = createContext({
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
});

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

const fetchAlertsSideEffect = (dispatch) => {
  dispatch(alertsActions.fetchAlertsRequest({
    status: 'active',
    show_scheduled: 'true',
    limit: 20,
  }));
};

function AuthProvider({ children }) {
  const dispatch = useDispatch();
  const authstate = useSelector((state) => state.auth);

  const fetchAndStoreUser = async () => {
    const response = await axios.get('/user/me/');
    const user = response.data;
    dispatch(actionsAuth.setUser(user));
    identifyUser(user);
    fetchAlertsSideEffect(dispatch);
    return user;
  };

  // Load user from stored tokens. Called on mount.
  const initUser = async () => {
    try {
      const access = localStorage.getItem('access');
      const refresh = localStorage.getItem('refresh');

      if (!(access && isValidToken(access)) && !refresh) {
        return;
      }

      await fetchAndStoreUser();
    } catch (e) {
      // Token invalid or backend unreachable — axios interceptor tried refresh.
      // If we end up here, credentials are no longer usable.
      setSession(null);
    } finally {
      dispatch(actionsAuth.initialize());
    }
  };

  useEffect(() => {
    initUser();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post('/token/obtain/', { email, password });
    if ('ERROR' in response.data) {
      throw new Error('ocurrio un error');
    }
    const { access, refresh } = response.data;
    setSession(access, refresh);
    await fetchAndStoreUser();
    track('user_logged_in', { method: 'email' });
    dispatch(actionsAuth.initialize());
  };

  const logout = () => {
    track('user_logged_out');
    resetAnalytics();
    setSession(null);
    dispatch(actionsAuth.logoutRequest());
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { access, user } = response.data;
    localStorage.setItem('access', access);
    identifyUser(user);
    track('user_registered', { method: 'email' });
    dispatch({ type: 'REGISTER', payload: { user } });
  };

  return (
    <AuthContext.Provider
      value={{
        ...authstate,
        method: 'jwt',
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
